import React from 'react';
import { Users, MapPin, School, Trophy, Activity, UserCheck, Target, Building } from 'lucide-react';

function fmt(n) {
  if (!n && n !== 0) return '0';
  return Number(n).toLocaleString();
}

const PATHWAY_KEYS = [
  'modified_male',
  'modified_female',
  'junior_male',
  'junior_female',
  'senior_male',
  'senior_female',
];

function sumPathwayForRegion(r) {
  return PATHWAY_KEYS.reduce((s, k) => s + (Number(r[k]) || 0), 0);
}

/**
 * Top KPIs split into Involvement (Data Capture sessions only) vs Competitions (all other ICC sheets).
 *
 * Involvement column MUST NOT use `summary` — that row mixes seeded roll-ups. Use only fields derived
 * from `kwata_final_sessions`, `kwata_final_schools` (session-derived), and `kwata_final_development_officers`
 * (aggregated from DelivererName on sessions), i.e. `_sessions`, `_schools`, `development_officers`, and
 * client-computed session aggregates on `data`.
 *
 * @param {{ data: object }} props — fetchDashboardData() result
 */
export default function KpiCards({ data }) {
  const { summary, regions, sessions_by_region: sessionsByRegion, involvement_total_participants: invNational } = data;

  const sessionRows = (data._sessions || []).length;
  const schoolsFromSessions = (data._schools || []).length;
  const devOfficerCount = (data.development_officers || []).length;

  const involvementActiveRegions = (sessionsByRegion || []).filter(
    (s) => s.region && s.region !== 'Unknown' && (s.total || 0) > 0
  ).length;

  const pathwayRowTotal = (regions || []).reduce((s, r) => s + sumPathwayForRegion(r), 0);

  const competitionActiveRegions = (regions || []).filter((r) => sumPathwayForRegion(r) > 0).length;

  const involvementCards = [
    {
      label: 'Total participants',
      value: fmt(invNational != null ? invNational : 0),
      icon: Activity,
      color: 'green',
      sub: 'Data Capture export (all rows)',
    },
    {
      label: 'Session reports',
      value: fmt(data.involvement_session_report_count),
      icon: Building,
      color: 'blue',
      sub: `${fmt(data.involvement_total_num_sessions)} delivery instances (Σ NumSessions)`,
    },
    {
      label: 'Schools / orgs',
      value: fmt(schoolsFromSessions),
      icon: School,
      color: 'sky',
      sub: 'Distinct org+region from session extract (kwata_final_schools)',
    },
    {
      label: 'Regions with involvement',
      value: `${involvementActiveRegions}/14`,
      icon: MapPin,
      color: 'green',
      sub: 'Named region with session participant total > 0',
    },
    {
      label: 'Development officers',
      value: fmt(devOfficerCount),
      icon: UserCheck,
      color: 'amber',
      sub: `${fmt(sessionRows)} rows in session extract`,
    },
  ];

  const competitionCards = [
    {
      label: 'Competition entries',
      value: fmt(summary?.total_competition_entries),
      icon: Trophy,
      color: 'blue',
      sub: `${fmt(summary?.unique_teams)} unique teams · ${fmt(summary?.unique_competitions)} competitions`,
    },
    {
      label: 'Pathway-classified rows',
      value: fmt(pathwayRowTotal),
      icon: Users,
      color: 'red',
      sub: 'Modified / Junior / Senior × gender (competition templates)',
    },
    {
      label: 'People registered',
      value: fmt(summary?.total_people),
      icon: UserCheck,
      color: 'amber',
      sub: 'Coaches, umpires, teachers (people template)',
    },
    {
      label: 'Matches played',
      value: fmt(summary?.total_matches),
      icon: Target,
      color: 'blue',
      sub: `${summary?.total_grounds || 0} grounds · ${summary?.total_clubs || 0} clubs (CricClubs)`,
    },
    {
      label: 'Regions with competitions',
      value: `${competitionActiveRegions}/14`,
      icon: MapPin,
      color: 'green',
      sub: 'At least one pathway-classified competition row',
    },
  ];

  const renderCard = (c, i, prefix) => (
    <div
      key={`${prefix}-${i}`}
      className="glass-card kpi-card interactive"
      data-testid={`kpi-${prefix}-${c.label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className={`kpi-icon ${c.color}`}>
        <c.icon size={22} />
      </div>
      <div>
        <div className="kpi-value">{c.value}</div>
        <div className="kpi-label">{c.label}</div>
        {c.sub && <div className="kpi-sub">{c.sub}</div>}
      </div>
    </div>
  );

  return (
    <div className="kpi-split" data-testid="kpi-grid">
      <div className="kpi-section">
        <h3 className="kpi-section-title">Involvement</h3>
        <p className="kpi-section-desc">Final 2025 Data capture tool export only.</p>
        <div className="kpi-grid">{involvementCards.map((c, i) => renderCard(c, i, 'inv'))}</div>
      </div>
      <div className="kpi-section">
        <h3 className="kpi-section-title">Competitions</h3>
        <p className="kpi-section-desc">Competition templates, people register, CricClubs — not mixed with involvement.</p>
        <div className="kpi-grid">{competitionCards.map((c, i) => renderCard(c, i, 'comp'))}</div>
      </div>
    </div>
  );
}
